/**
 * KinNetwork — public "Kerala Kin Network" landing page.
 *
 * Renders a live SVG word-cloud of the top 50 most-matched surnames
 * in the past window (default 90 days). Each surname links to a
 * sample tree so anonymous visitors immediately land inside the
 * network. Sits at /kin-network on IndianFamilyTree.com.
 *
 * Powered by: GET /api/ancestor-matches/public/top-surnames
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "@/lib/api";
import { Users, Sparkles, ArrowLeft, Loader2 } from "lucide-react";

const P = {
  saffron:     "#E48A1F",
  saffronSoft: "#F4D3A5",
  green:       "#0F6B45",
  cream:       "#FFF8EE",
  ink:         "#1B1B1B",
  inkSoft:     "#5B4A3A",
};

const WINDOWS = [
  { key: 30,  label: "30 days" },
  { key: 90,  label: "90 days" },
  { key: 180, label: "6 months" },
  { key: 365, label: "12 months" },
];

export default function KinNetwork() {
  const [windowDays, setWindowDays] = useState(90);
  const [state, setState] = useState({ loading: true, items: [] });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    http.get("/ancestor-matches/public/top-surnames", {
      params: { window_days: windowDays, limit: 50, site: "indianfamilytree" },
    })
      .then((r) => alive && setState({ loading: false, items: r.data?.items || [] }))
      .catch(() => alive && setState({ loading: false, items: [] }));
    return () => { alive = false; };
  }, [windowDays]);

  return (
    <div style={{ background: P.cream, color: P.ink, minHeight: "100vh" }} data-testid="kin-network-page">
      <Helmet>
        <title>Kin Network — India's live surname discovery cloud</title>
        <meta name="description"
              content="Live word-cloud of the surnames most frequently matched across family trees on IndianFamilyTree.com. Tap any surname to jump into a tree." />
      </Helmet>

      <header className="max-w-6xl mx-auto px-4 pt-6 pb-4">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm opacity-70 hover:opacity-100"
              data-testid="kin-back">
          <ArrowLeft className="w-4 h-4" /> IndianFamilyTree.com
        </Link>
      </header>

      <section className="max-w-6xl mx-auto px-4 pb-10 text-center">
        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4"
             style={{ background: "#FFF3E0", color: P.saffron }}>
          <Sparkles className="w-3.5 h-3.5" />
          <span className="text-[11px] font-mono uppercase tracking-wider">Live network</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold" style={{ color: P.ink }}>
          The Kin Network
        </h1>
        <p className="mt-3 text-base" style={{ color: P.inkSoft }}>
          The bigger the surname, the more times we've matched its ancestors
          across different family trees. Tap any surname to jump straight in.
        </p>

        <div className="mt-6 inline-flex rounded-full border overflow-hidden"
             style={{ borderColor: P.saffronSoft }} data-testid="kin-window-selector">
          {WINDOWS.map((w) => {
            const on = w.key === windowDays;
            return (
              <button
                key={w.key}
                onClick={() => setWindowDays(w.key)}
                className="px-4 py-1.5 text-xs font-semibold transition"
                style={{
                  background: on ? P.saffron : "transparent",
                  color:      on ? "#fff"     : P.inkSoft,
                }}
                data-testid={`kin-window-${w.key}`}
              >
                {w.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        {state.loading ? (
          <div className="py-24 text-center opacity-60" data-testid="kin-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : state.items.length === 0 ? (
          <EmptyState />
        ) : (
          <SurnameCloud items={state.items} />
        )}
      </section>
    </div>
  );
}


function EmptyState() {
  return (
    <div className="max-w-md mx-auto text-center py-16"
         style={{ color: P.inkSoft }} data-testid="kin-empty">
      <Users className="w-10 h-10 mx-auto opacity-40 mb-3" />
      <h3 className="font-display text-xl mb-2" style={{ color: P.ink }}>
        The network is just getting started
      </h3>
      <p className="text-sm">
        We haven't detected enough cross-tree matches in this window yet.
        As more families upload their trees, this cloud will fill up with
        the surnames connecting the most branches.
      </p>
      <Link to="/family-trees/new?import=gedcom"
            className="inline-block mt-4 rounded-full px-5 py-2 text-white text-sm font-semibold"
            style={{ background: P.green }}
            data-testid="kin-upload-gedcom">
        Upload your family tree
      </Link>
    </div>
  );
}


function SurnameCloud({ items }) {
  // Compute font sizes: log scale between 14px and 56px based on count.
  const { minCount, maxCount } = useMemo(() => {
    const counts = items.map((i) => i.count);
    return { minCount: Math.min(...counts), maxCount: Math.max(...counts) };
  }, [items]);
  const sizeFor = (n) => {
    if (maxCount === minCount) return 26;
    const ratio = (Math.log(n) - Math.log(minCount)) / (Math.log(maxCount) - Math.log(minCount) || 1);
    return Math.round(14 + ratio * 42);
  };
  const colorFor = (strength) => {
    if (strength === "exact")  return P.saffron;
    if (strength === "strong") return P.green;
    return P.inkSoft;
  };

  return (
    <div
      className="rounded-2xl border p-6 sm:p-10 flex flex-wrap gap-x-4 gap-y-3 items-baseline justify-center"
      style={{ background: "#fff", borderColor: P.saffronSoft }}
      data-testid="kin-cloud"
    >
      {items.map((it) => (
        <Link
          key={it.surname + it.sample_tree.slug}
          to={`/family/${it.sample_tree.slug}`}
          className="inline-block font-display transition hover:opacity-70"
          style={{
            fontSize: `${sizeFor(it.count)}px`,
            color:    colorFor(it.strength),
            lineHeight: 1.1,
          }}
          title={`${it.count} matches · sample: ${it.sample_tree.name}${it.state ? " · " + it.state : ""}`}
          data-testid={`kin-surname-${it.surname.toLowerCase()}`}
        >
          {it.surname}
          <sup className="text-[10px] font-mono opacity-60 ml-0.5">{it.count}</sup>
        </Link>
      ))}
    </div>
  );
}
